import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Backpack, Map, ClipboardList, Zap, Clock, ChevronDown, Flame, Package, Sparkles, Volume2, VolumeX, Recycle } from 'lucide-react';
import { studentAPI } from '../utils/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import soundManager from '../utils/soundManager';

const MochilaMenu = ({ winStreak = 0, xpMultiplier = 1.0, onItemUsed, inventoryOnly = false }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(inventoryOnly ? 'inventory' : 'navigation');
  // pendingCount kept for potential future indicators inside the dropdown (e.g., missions tab)
  // — it is intentionally NOT included in the bag badge anymore. The bag badge reflects ONLY
  // new (unseen) items so it can be cleared the moment the student opens the menu.
  const [pendingCount, setPendingCount] = useState(0);
  const [unseenItems, setUnseenItems] = useState(0);
  const [inventory, setInventory] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [usingItem, setUsingItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isMuted, setIsMuted] = useState(soundManager.getMuted());
  const [sellConfirm, setSellConfirm] = useState(null);
  const [selling, setSelling] = useState(false);
  const menuRef = useRef(null);

  // Navigation items configuration - preserving original IDs
  const navigationItems = [
    {
      id: 'missions',
      testId: 'view-missions-btn',
      label: 'Missões',
      icon: Map,
      color: 'from-purple-500 to-indigo-600',
      iconColor: 'text-purple-500',
      path: '/student/missions',
      description: 'Quizzes e desafios'
    },
    {
      id: 'tasks',
      testId: 'view-tasks-btn',
      label: 'Tarefas',
      icon: ClipboardList,
      color: 'from-blue-500 to-cyan-600',
      iconColor: 'text-blue-500',
      path: '/student/tasks',
      description: 'Tarefas do professor'
    },
    {
      id: 'powers',
      testId: 'view-powers-btn',
      label: 'Poderes',
      icon: Zap,
      color: 'from-yellow-500 to-orange-500',
      iconColor: 'text-yellow-500',
      path: '/student/powers',
      description: 'Habilidades especiais'
    },
    {
      id: 'history',
      testId: 'view-history-btn',
      label: 'Histórico',
      icon: Clock,
      color: 'from-green-500 to-emerald-600',
      iconColor: 'text-green-500',
      path: '/student/history',
      description: 'Sua jornada'
    }
  ];

  // Fetch pending count and inventory
  const fetchData = useCallback(async () => {
    try {
      const [pendingRes, inventoryRes] = await Promise.all([
        studentAPI.getPendingCount(),
        studentAPI.getInventory()
      ]);
      
      setPendingCount(pendingRes.data.total_pending || 0);
      setUnseenItems(inventoryRes.data.unseen_count || 0);
      setInventory(inventoryRes.data.items || []);
    } catch (error) {
      console.error('Failed to fetch mochila data:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Mark items as seen when opening the menu (any tab) so the badge clears immediately.
  useEffect(() => {
    if (isExpanded && unseenItems > 0) {
      studentAPI.markInventorySeen().then(() => {
        setUnseenItems(0);
        // Refetch to update badges
        fetchData();
      }).catch(console.error);
    }
  }, [isExpanded, unseenItems, fetchData]);

  // Handle ESC key to close menu
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isExpanded]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isExpanded && menuRef.current && !menuRef.current.contains(e.target)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  // Handle navigation
  const handleNavigate = useCallback((path) => {
    soundManager.playButtonClick();
    setIsExpanded(false);
    soundManager.playMenuClose();
    navigate(path);
  }, [navigate]);

  // Handle tab switch
  const handleTabSwitch = (tab) => {
    soundManager.playTabSwitch();
    setActiveTab(tab);
  };

  // Toggle sound mute
  const handleToggleMute = (e) => {
    e.stopPropagation();
    const newMuted = soundManager.toggleMute();
    setIsMuted(newMuted);
    if (!newMuted) {
      soundManager.playButtonClick();
    }
  };

  // Handle item use
  const handleUseItem = async (item) => {
    if (usingItem) return;
    
    setUsingItem(item.inventory_id);
    
    // Play item use sound immediately for instant feedback
    soundManager.playItemUse();
    
    try {
      const response = await studentAPI.useItem(item.inventory_id);
      
      // Play specific effect sound based on item type
      if (item.effect_type === 'level') {
        soundManager.playLevelUp();
      } else {
        soundManager.playHealEffect();
      }
      
      // Success animation
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.6 },
        colors: item.rarity === 'rare' ? ['#FFD700', '#FF69B4'] : ['#10B981', '#3B82F6']
      });
      
      // Show success toast
      toast.success(response.data.message, { 
        icon: item.emoji,
        duration: 3000 
      });
      
      // Remove from local inventory
      setInventory(prev => prev.filter(i => i.inventory_id !== item.inventory_id));
      
      // Callback for parent to update stats
      if (onItemUsed) {
        onItemUsed(response.data);
      }
      
    } catch (error) {
      console.error('Failed to use item:', error);
      soundManager.playError();
      toast.error(error.response?.data?.detail || 'Erro ao usar item');
    } finally {
      setUsingItem(null);
    }
  };

  // Handle item sell
  const handleSellItem = async (item) => {
    setSelling(true);
    // Optimistic update: remove item from UI immediately
    setInventory(prev => prev.filter(i => i.inventory_id !== item.inventory_id));
    // Optimistic callback: notify parent of +2 coins immediately
    if (onItemUsed) onItemUsed({ coins_earned: 2, optimistic: true });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student/inventory/sell`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ inventory_id: item.inventory_id })
      });
      if (response.ok) {
        const data = await response.json();
        soundManager.playButtonClick();
        toast.success(data.message);
        // Send confirmed coins to parent
        if (onItemUsed) onItemUsed(data);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao vender');
        // Rollback: re-add item on failure
        setInventory(prev => [...prev, item]);
        if (onItemUsed) onItemUsed({ rollback: true });
      }
    } catch (e) {
      toast.error('Erro ao vender item');
      // Rollback
      setInventory(prev => [...prev, item]);
      if (onItemUsed) onItemUsed({ rollback: true });
    } finally {
      setSelling(false);
      setSellConfirm(null);
    }
  };

  // Toggle menu with sound
  const toggleMenu = () => {
    if (!isExpanded) {
      soundManager.playMenuOpen();
    } else {
      soundManager.playMenuClose();
    }
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setActiveTab(inventoryOnly ? 'inventory' : 'navigation');
    }
  };

  // Calculate total badge count
  const totalBadge = unseenItems;

  return (
    <div 
      ref={menuRef}
      className="relative"
      data-testid="mochila-menu-container"
    >
      {/* Header Button */}
      <button
        data-testid="mochila-fab"
        onClick={toggleMenu}
        className={`
          relative flex items-center gap-1.5 
          px-3 py-2 rounded-xl
          bg-gradient-to-r from-amber-500 to-orange-500
          hover:from-amber-600 hover:to-orange-600
          text-white font-semibold text-sm
          shadow-md hover:shadow-lg
          transition-all duration-200
          ${isExpanded ? 'ring-2 ring-orange-300 ring-offset-1' : ''}
          ${winStreak >= 2 ? 'animate-pulse-fire-subtle' : ''}
        `}
        aria-label="Mochila do Treinador"
        aria-expanded={isExpanded}
      >
        <Backpack className="w-5 h-5" />
        <span className="hidden sm:inline">Mochila</span>
        <ChevronDown 
          className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />

        {/* Combined Badge */}
        {totalBadge > 0 && (
          <div 
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1
                       rounded-full bg-red-500 
                       flex items-center justify-center
                       text-white text-[10px] font-bold
                       border-2 border-white
                       animate-badge-pulse"
            data-testid="mochila-badge"
          >
            {totalBadge > 99 ? '99+' : totalBadge}
          </div>
        )}

        {/* Win Streak Fire */}
        {winStreak >= 2 && (
          <span className="text-sm ml-0.5">🔥</span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isExpanded && (
        <div 
          className="absolute top-full right-0 mt-2 w-80 
                     rounded-2xl overflow-hidden
                     animate-dropdown-expand z-50"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.95) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)'
          }}
        >
          {/* Tab Headers */}
          {!inventoryOnly && (
          <div className="flex border-b border-slate-100">
            <button
              onClick={() => setActiveTab('navigation')}
              className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors
                         ${activeTab === 'navigation' 
                           ? 'text-orange-600 border-b-2 border-orange-500 bg-orange-50/50' 
                           : 'text-slate-500 hover:text-slate-700'}`}
            >
              <span className="flex items-center justify-center gap-2">
                <Map className="w-4 h-4" />
                Navegação
              </span>
            </button>
            <button
              onClick={() => handleTabSwitch('inventory')}
              className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors relative
                         ${activeTab === 'inventory' 
                           ? 'text-purple-600 border-b-2 border-purple-500 bg-purple-50/50' 
                           : 'text-slate-500 hover:text-slate-700'}`}
            >
              <span className="flex items-center justify-center gap-2">
                <Package className="w-4 h-4" />
                Meus Itens
                {unseenItems > 0 && (
                  <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center animate-pulse">
                    {unseenItems}
                  </span>
                )}
              </span>
            </button>
          </div>
          )}

          {/* Inventory-only header */}
          {inventoryOnly && (
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Package className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-bold text-slate-700">Meus Itens</span>
            {unseenItems > 0 && (
              <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center animate-pulse">
                {unseenItems}
              </span>
            )}
          </div>
          )}

          {/* Win Streak Header */}
          {winStreak >= 2 && (
            <div 
              className="px-4 py-2 border-b border-slate-100"
              style={{
                background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1), rgba(239, 68, 68, 0.1))'
              }}
            >
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500 animate-pulse" />
                <span className="text-sm font-bold text-orange-600">
                  Combo x{winStreak}
                </span>
                <span className="text-xs text-orange-500/80">
                  +{Math.round((xpMultiplier - 1) * 100)}% AURA
                </span>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="max-h-80 overflow-y-auto">
            {/* Navigation Tab - only when not inventoryOnly */}
            {!inventoryOnly && activeTab === 'navigation' && (
              <div className="py-2">
                {navigationItems.map((item, index) => {
                  const Icon = item.icon;
                  const isHovered = hoveredItem === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      data-testid={item.testId}
                      onClick={() => handleNavigate(item.path)}
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`
                        w-full flex items-center gap-3 px-4 py-3
                        transition-all duration-200
                        ${isHovered ? 'bg-slate-50' : ''}
                      `}
                    >
                      <div 
                        className={`
                          w-10 h-10 rounded-xl 
                          bg-gradient-to-br ${item.color}
                          flex items-center justify-center shadow-sm
                          transition-transform duration-200
                          ${isHovered ? 'scale-110' : ''}
                        `}
                      >
                        <Icon className={`w-5 h-5 text-white ${isHovered ? 'animate-bounce-icon' : ''}`} />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-semibold text-slate-800 text-sm">{item.label}</div>
                        <div className="text-xs text-slate-500">{item.description}</div>
                      </div>
                      <div className={`text-slate-400 transition-all duration-200
                                      ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
                        →
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Inventory Tab */}
            {(inventoryOnly || activeTab === 'inventory') && (
              <div className="p-4">
                {inventory.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500 text-sm font-medium">Sua mochila está vazia</p>
                    <p className="text-slate-400 text-xs mt-1">
                      Acerte 3/3 questões para ganhar itens!
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {inventory.map((item) => (
                      <div
                        key={item.inventory_id}
                        data-testid={`inventory-item-${item.item_type}`}
                        className={`
                          relative rounded-xl p-3
                          bg-gradient-to-br ${item.color}
                          shadow-md transition-all duration-200
                          ${usingItem === item.inventory_id ? 'animate-item-use' : 'hover:scale-105 hover:shadow-lg'}
                          ${!item.seen ? 'ring-2 ring-yellow-400 ring-offset-2' : ''}
                        `}
                      >
                        {/* New badge */}
                        {!item.seen && (
                          <div className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full bg-yellow-400 text-[9px] font-bold text-yellow-900">
                            NOVO
                          </div>
                        )}

                        {/* Rare star */}
                        {item.rarity === 'rare' && (
                          <Sparkles className="absolute top-2 right-2 w-4 h-4 text-yellow-300 animate-pulse" />
                        )}

                        {/* Item emoji */}
                        <div className="text-4xl mb-2 text-center">{item.emoji}</div>

                        {/* Item name */}
                        <div className="text-white font-bold text-sm text-center truncate">
                          {item.name}
                        </div>

                        {/* Description */}
                        <div className="text-white/80 text-[10px] text-center mb-2">
                          {item.description}
                        </div>

                        {/* Sell confirmation overlay */}
                        {sellConfirm === item.inventory_id && (
                          <div className="absolute inset-0 bg-black/70 rounded-xl flex flex-col items-center justify-center p-2 z-10 backdrop-blur-sm">
                            <p className="text-white text-[11px] font-bold text-center mb-2">
                              Vender 1x {item.name} por 2 Coins?
                            </p>
                            <div className="flex gap-2">
                              <button
                                data-testid={`confirm-sell-${item.item_type}`}
                                onClick={() => handleSellItem(item)}
                                disabled={selling}
                                className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg transition-colors"
                              >
                                {selling ? '...' : 'Sim'}
                              </button>
                              <button
                                data-testid={`cancel-sell-${item.item_type}`}
                                onClick={() => setSellConfirm(null)}
                                className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-xs font-bold rounded-lg transition-colors"
                              >
                                Não
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => handleUseItem(item)}
                            disabled={usingItem !== null}
                            className={`
                              flex-1 py-1.5 rounded-lg text-xs font-bold
                              transition-all duration-200
                              ${usingItem === item.inventory_id
                                ? 'bg-white/30 text-white/70 cursor-wait'
                                : 'bg-white/90 text-slate-800 hover:bg-white'}
                            `}
                          >
                            {usingItem === item.inventory_id ? 'Usando...' : 'Usar'}
                          </button>
                          {item.sellable !== false && (
                            <button
                              data-testid={`sell-${item.item_type}`}
                              onClick={() => setSellConfirm(item.inventory_id)}
                              disabled={usingItem !== null || selling}
                              className="py-1.5 px-2 rounded-lg text-xs font-bold bg-white/30 text-white hover:bg-white/50 transition-all"
                              title="Vender por 2 Coins"
                            >
                              <Recycle className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer with Sound Toggle */}
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <div className="text-[10px] text-slate-400">
                {activeTab === 'inventory' 
                  ? `${inventory.length} item(s) na mochila`
                  : 'Pressione ESC para fechar'}
              </div>
              
              {/* Sound Toggle Button */}
              <button
                onClick={handleToggleMute}
                className={`
                  flex items-center gap-1.5 px-2 py-1 rounded-lg
                  text-[10px] font-medium transition-all duration-200
                  ${isMuted 
                    ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100' 
                    : 'text-orange-500 hover:text-orange-600 hover:bg-orange-50'}
                `}
                title={isMuted ? 'Ativar sons' : 'Silenciar sons'}
                data-testid="sound-toggle-btn"
              >
                {isMuted ? (
                  <>
                    <VolumeX className="w-3.5 h-3.5" />
                    <span>Mudo</span>
                  </>
                ) : (
                  <>
                    <Volume2 className="w-3.5 h-3.5" />
                    <span>Som</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MochilaMenu;
